define("Inputs/Textbox", [`Input`, `module`], (Super, Self) => {
	return Super.inherit({
		namespace: Self.id,
		constructor(setup) {
			// Ingest the rules
			const { data, elements } = this;
			const { rules, validation } = data;
			const { style } = setup;

			this.defaults(validation, { type: `String` });

			const DEFAULT_MAX_LENGTH = 500;
			const { max_length, required, type } = validation;
			const maxLength = this.isInt(max_length, 0) ? max_length : DEFAULT_MAX_LENGTH;
			let randomNumber;
			let inputType;
			let input;

			// Sets a specific style for the textbox, currently only support ux4-search
			// see Textbox.styl for more details about the changes for the style
			if (this.isString(style)) {
				this.container.dataset.style = style;
			}

			switch (type) {
				case `Password`:
					inputType = `password`;
					break;

				case `UnsignedIntString`:
					inputType = `tel`;
					break;

				default:
					inputType = `text`;
			}

			this.defaults(rules, {
				autocomplete: true,
				gracePeriod: true,
				inputType,
				isCode: false, // whether to capitalize everything
				forceCaps: false, // forces uppercase
				autoRemovePattern: null, // auto-removes chars on keyup based on regex pattern if provided
				tooltip: null,
				tooltipDirection: `top`,
				tooltipLabel: false, // whether to use label as a default tooltip
				prefix: null, // adds a prefix to the textbox
				suffix: null, // adds a suffix to the textbox
				allowShowPassword: false, // shows the show password icon (only available for password inputs)
				actions: [], // custom actions for the textbox
				accessibilityId: null, // e.g. emailSignup, emailLogin, confirmEmailSignup, passwordSignup, passwordLogin - used for creating a unique static Id to associate the label to the textbox, should be UNIQUE on a given page
				hasClear: false, // whether to show a clear button when there is text in the input
			});

			if (!rules.autocomplete) {
				// don't bother calculating unless it will be used to anonymize the internal input
				randomNumber = Math.random();
			}

			if (type === `Password` && rules.allowShowPassword) {
				this.addShowPasswordAction();
			}

			if (rules.hasClear) {
				rules.actions.push({
					name: `clear`,
					tooltip: `Clear`,
					icon: `close`,
					variant: `filled`,
					handler: (evt) => {
						this.setValue(``, false, true);
						this.syncValue({
							endGracePeriod: true,
							forceSet: true,
						});
						this.checkTooltip();
						this.validate({ triggerErrors: false });
						this.elements.input.focus();
					},
				});
			}

			if (rules.actions.length) {
				this.addClass(`actions-${rules.actions.length}`);

				if (rules.actions.length > 2) {
					this.console({
						type: `warn`,
						message: `Textbox: Only two actions are supported.`,
					});
				}
			}

			this.assign(data, {
				// NOTE: this is not just a convenience passthrough, error message can dynamically change
				errorMessage: this.isString(rules.errorMessage, 1) ? rules.errorMessage : null,
				errorShowing: false,
				firstFocus: rules.gracePeriod,
				gracePeriod: rules.gracePeriod,
				hasError: false,
				hasFocus: false,
				keyUpCache: null,
				randomNumber,
			});

			this.render();

			input = this.get(`input`);
			input.setAttribute(`maxlength`, maxLength + 1);

			if (required) {
				input.setAttribute(`required`, `required`);
			}

			elements.input = input;
			elements.icon = this.get(`.left .icon`);

			this.checkLabel();
			this.updateRules(rules);
		},

		listeners: [
			{
				event: `focus`, // The input has received focus
				filter: `input`,
				handler(evt) {
					// update visually and show any tooltips, if necessary
					this.addClass(`focus`);
					this.data.hasFocus = true;

					this.checkTooltip();

					if (this.isFunction(this.callbacks.focus)) {
						this.callbacks.focus(evt, this.name);
					}
				},
			},
			{
				event: `blur`,
				filter: `input`,
				handler(evt) {
					this.removeClass(`focus`);
					this.data.hasFocus = false;

					if (this.isFunction(this.callbacks.blur)) {
						this.callbacks.blur(evt);
					}

					this.syncValue({
						endGracePeriod: true,
						forceSet: true,
					});
					this.checkTooltip();
					this.validate({ triggerErrors: true });
				},
			},
			{
				event: `paste`,
				filter: `input`,
				handler(evt) {
					window.setTimeout(() => {
						this.syncValue({
							endGracePeriod: true,
							forceSet: true,
						});
						this.checkTooltip();
						this.validate({ triggerErrors: true });
					});
				},
			},
			{
				event: `keydown`,
				filter: `input`,
				handler(evt) {
					// Capture a cache of the input value on keydown (before the struck key affects it)
					if (evt.keyCode === 13) {
						// || evt.keyCode === 9) {
						this.syncValue({
							endGracePeriod: false,
						});
						this.onSubmit();
					}
				},
			},
			{
				event: `keyup`, // keydown in the input box
				filter: `input`,
				handler(evt) {
					// Update the internal value, and use the cached keydown value to figure out what to do
					evt.preventDefault();
					this.syncValue({
						endGracePeriod: false,
					});
				},
			},
			{
				event: `click`,
				handler(evt) {
					// Capture all clicks, so that they focus on the textbox
					if (!this.hasClass(`disabled`)) {
						this.elements.input.focus();
					}
				},
			},
			{
				event: `change`,
				filter: `input`,
				handler() {
					if (this.data.firstFocus) {
						// Value changed early on, may be form pre-population
						this.syncValue({
							endGracePeriod: true,
							forceSet: true,
						});
					}
				},
			},
			{
				event: `mouseenter`,
				handler(evt) {
					if (this.hasClass(`disabled`)) {
						// tooltip handler when the input is disabled
						this.data.hasFocus = true;
						this.checkTooltip();
					}
				},
			},
			{
				event: `mouseleave`,
				handler(evt) {
					if (this.hasClass(`disabled`)) {
						// tooltip handler when the input is disabled
						this.data.hasFocus = false;
						this.checkTooltip();
					}
				},
			},
			{
				event: `click`,
				filter: `[data-name]`,
				handler: function (evt) {
					const { data } = this;
					const { rules } = data;
					const name = evt.currentTarget.dataset.name;
					const actionHandler = this.find(rules.actions, { name });

					if (actionHandler) {
						// prevent focusing on input
						evt.preventDefault();
						evt.stopPropagation();
						actionHandler.handler(evt);
					}
				},
			},
		],

		api: {
			syncValue: {
				value(options) {
					// sync the internal value with what's being displayed in the text input
					var data = this.data,
						value = this.getValue(),
						cachedValue = data.keyUpCache, // the last recorded value, before this keystroke
						inputValue = this.elements.input.value, // the contents of the text input
						scrubbedValue,
						skipCallbacks,
						forceSet;

					options = this.defaults(options, {
						endGracePeriod: false,
						forceSet: false,
					});
					forceSet = options.forceSet;

					skipCallbacks = !forceSet && data.firstFocus; // whether to skip calling the onChange callback

					if (options.endGracePeriod) {
						data.gracePeriod = false;
					}

					data.keyUpCache = inputValue;
					if (inputValue.length > 0) {
						this.addClass(`has-text`);
					} else {
						this.removeClass(`has-text`);
					}

					switch (
						data.validation.type // special handling based on validation type
					) {
						case `Number`:
							scrubbedValue = inputValue.replace(/[^0-9.-]/gi, ``); // remove any non-float characters
							if (scrubbedValue !== inputValue) {
								// something was removed
								forceSet = true;
							}

							inputValue = parseFloat(scrubbedValue);
							if (!this.isFinite(inputValue)) {
								inputValue = null;
							}

							cachedValue = parseFloat(cachedValue);
							if (!this.isFinite(cachedValue)) {
								cachedValue = null;
							}
							break;

						case `Integer`:
							scrubbedValue = inputValue.replace(/[^0-9-]/gi, ``); // remove any non-int characters
							if (scrubbedValue !== inputValue) {
								// something was removed
								forceSet = true;
							}

							inputValue = parseInt(scrubbedValue);
							if (!this.isInt(inputValue)) {
								inputValue = null;
							}

							cachedValue = parseInt(cachedValue);
							if (!this.isInt(cachedValue)) {
								cachedValue = null;
							}
							break;

						case `String`:
							if (this.isString(inputValue)) {
								const { rules } = data;
								const { caseFormat, isCode } = rules;

								if (isCode) {
									inputValue = inputValue.toUpperCase().replace(/[^A-Z0-9]/g, ``);
								}

								if (data.rules.forceCaps && this.isString(inputValue)) {
									inputValue = inputValue.toUpperCase();
								}

								if (this.isObject(data.rules.autoRemovePattern) && this.isString(inputValue)) {
									inputValue = inputValue.replace(data.rules.autoRemovePattern, ``);
								}

								if (this.isString(caseFormat, 1)) {
									if (caseFormat === `UPPER`) {
										inputValue = inputValue.toUpperCase();
									} else if (caseFormat === `LOWER`) {
										inputValue = inputValue.toLowerCase();
									} else {
										throw new Error(`Invalid use of rules.caseFormat: "${caseFormat}"`);
									}
								}
							}
							break;
					}

					if (!forceSet && this.isEqual(inputValue, cachedValue)) {
						skipCallbacks = true;
					}

					if (!skipCallbacks) {
						data.firstFocus = false; // Initial value setting complete, start validating on keyup
					}

					if (inputValue === ``) {
						inputValue = null; // Input[validation.type="String"] returns null for 'empty'
					}

					if (forceSet || !this.isEqual(value, inputValue)) {
						this.setValue(inputValue, skipCallbacks);
					} else {
						this.checkLabel();
					}
				},
			},

			updateRules: {
				public: true,
				value(context, returned, updates) {
					const { data, elements } = this;
					const { rules } = data;
					const done = true;
					const label = this.get(`.label`);
					const iconWrapper = this.get(`.icon-container.left`);
					const prefix = this.get(`.prefix`);
					const suffix = this.get(`.suffix`);

					// NOTE: don't need to apply the updates, only extending Input.updateRules()
					label.innerHTML = this.isString(rules.label, 1) ? rules.label : ``;

					if (this.isString(rules.prefix, 1)) {
						if (this.isString(rules.icon, 1) || this.isString(rules.label, 1)) {
							throw new Error(`Prefix is not supported if icon or label are defined`);
						}

						prefix.innerHTML = rules.prefix;
						this.addClass(`has-prefix`);
					} else {
						this.removeClass(`has-prefix`);
					}

					if (this.isString(rules.suffix, 1)) {
						suffix.innerHTML = rules.suffix;
						this.addClass(`has-suffix`);
					} else {
						this.removeClass(`has-suffix`);
					}

					if (this.isString(rules.icon, 1)) {
						const { icon } = elements;

						// An icon is present, add a class for styling, and insert it into templating data
						icon.className = `icon icon-${rules.icon}`;

						this.addClass(`has-left-icon`);
						this.show({ target: iconWrapper, done });
					} else {
						this.removeClass(`has-left-icon`);
						this.hide({ target: iconWrapper, done });
					}
				},
			},

			checkLabel: {
				value() {
					if (this.elements.input.value !== ``) {
						if (this.data.hasFocus) {
							this.checkTooltip();
						}
					} else {
						this.checkTooltip();
					}
				},
			},

			checkTooltip: {
				value() {
					var data = this.data,
						rules = data.rules,
						params;

					if (!data.hasFocus) {
						data.tooltip = null;
						this.App.hideTooltip();
						return;
					}

					if (data.hasError && !data.gracePeriod) {
						// an error should be shown
						if (this.isString(data.errorMessage, 1)) {
							params = {
								message: data.errorMessage,
								type: `error`,
							};
						} else if (rules.tooltipLabel && this.isString(rules.label, 1)) {
							// no specific error message, show an angry verson of the label
							if (this.DOM.hasClass(`has-text`)) {
								// don't show if the label is already displaying the label
								params = {
									message: rules.label,
									type: `error`,
								};
							}
						}
					} else if (!data.enabled && this.isString(rules.disabledMessage, 1)) {
						// input is disabled and there is a specific tooltip for it
						params = {
							message: rules.disabledMessage,
							type: `message`,
						};
					} else if (this.isString(rules.tooltip, 1)) {
						// no error, and we have a specific tooltip to show
						params = {
							message: rules.tooltip,
							type: `message`,
						};
					} else if (rules.tooltipLabel && this.isString(rules.label, 1)) {
						// no specific tooltip, show the label if it's allowed
						if (this.DOM.hasClass(`has-text`)) {
							// don't show if the label is already displaying the label
							params = {
								message: rules.label,
								type: `message`,
								done: true,
							};
						}
					}

					if (this.isObject(params)) {
						// show the applicable tooltip
						params.direction = rules.tooltipDirection;
						if (!this.isEqual(data.tooltip, params)) {
							data.tooltip = this.clone(params);
							this.App.spawnTooltip(params);
						}
					} else {
						// hide any showing ones if we didn't find a reason
						data.tooltip = null;
						this.App.hideTooltip();
					}
				},
			},

			/**
			 * Add a custom action to the textbox
			 * @method addAction
			 * @param {Object} action
			 * @param {String} action.name - the name of the action
			 * @param {?String} action.tooltip - the tooltip to display
			 * @param {String} action.icon - the icon to display
			 * @param {Function} action.handler - the function to call when the action is clicked
			 */
			addAction: {
				value: function (action) {
					const { actions } = this.data.rules;
					actions.push(action);
				},
			},

			/**
			 * Add the show password action to the textbox
			 * @method addShowPasswordAction
			 */
			addShowPasswordAction: {
				value: function () {
					const action = {
						name: `toggle-show-password`,
						tooltip: `Toggle password visibility`,
						icon: `eye`,
						handler: (evt) => {
							const inputEl = this.get(`input`);
							const show = inputEl.type === `password`;
							this.setShowPassword(show);
						},
					};
					this.addAction(action);
				},
			},

			/**
			 * Toggle password visiblity (masking)
			 * @method setShowPassword
			 */
			setShowPassword: {
				public: true,
				value(context, value) {
					const inputEl = this.get(`input`);
					const iconEl = this.get(`.icon-eye-disabled`) || this.get(`.icon-eye`);

					if (value) {
						this.removeClass(`icon-eye`, iconEl);
						this.addClass(`icon-eye-disabled`, iconEl);
						inputEl.type = `text`;
					} else {
						this.removeClass(`icon-eye-disabled`, iconEl);
						this.addClass(`icon-eye`, iconEl);
						inputEl.type = `password`;
					}
				},
			},

			onValidateError: {
				public: true,
				value(context, returned, error, resetFocus) {
					var data = this.data;

					data.hasError = true;
					data.firstFocus = resetFocus === true;

					data.errorMessage = this.isString(error) ? error : data.rules.tooltip;
					this.checkTooltip();
				},
			},

			onValidateSuccess: {
				public: true,
				value(context, returned, resetFocus) {
					var data = this.data;

					data.hasError = false;
					data.firstFocus = resetFocus === true;
					this.checkTooltip();
				},
			},

			setValue: {
				public: true,
				value(context, returned, value, skipCallbacks, forceUpdateCache) {
					var rules = this.data.rules,
						displayValue;

					displayValue = this.isNull(returned) ? `` : `` + returned;

					if (this.elements.input.value !== displayValue) {
						if (this.isFinite(returned) && rules.format === `Currency`) {
							displayValue = returned.toFixed(2);
						}

						this.elements.input.value = displayValue;
					}

					this.checkLabel();

					if (displayValue === ``) {
						this.removeClass(`valid has-text`);
					} else if (displayValue.length > 0) {
						this.addClass(`has-text`);
					}

					if (forceUpdateCache) {
						this.data.keyUpCache = displayValue;
					}

					return returned;
				},
			},

			disable: {
				public: true,
				value(context) {
					this.elements.input.setAttribute(`disabled`, `disabled`);
				},
			},

			enable: {
				public: true,
				value(context) {
					this.elements.input.removeAttribute(`disabled`);
				},
			},

			focus: {
				public: true,
				value(context, returned) {
					// an overriding function
					this.elements.input.focus();
				},
			},

			blur: {
				public: true,
				value(context) {
					this.elements.input.blur();
				},
			},
		},
	});
});

define("Inputs/Textbox.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Inputs/Textbox/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " id=\"label-id-"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"accessibilityId") : stack1), depth0))
    + "\"";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " aria-labelledby=\"label-id-"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"accessibilityId") : stack1), depth0))
    + "\"";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " id=\"input-id-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"randomNumber") : depth0), depth0))
    + "\" name=\"input-name-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"randomNumber") : depth0), depth0))
    + "\" autocapitalize=\"off\" autocomplete=\"off\" autocorrect=\"off\"";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" data-name=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"tooltip") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":670},"end":{"line":1,"column":723}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"variant") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":723},"end":{"line":1,"column":765}}})) != null ? stack1 : "")
    + ">"
    + alias1(lookupProperty(helpers,"getIcon").call(alias2,(depth0 != null ? lookupProperty(depth0,"icon") : depth0),{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":766},"end":{"line":1,"column":782}}}))
    + "</a> ";
},"8":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "data-tooltip-text=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"tooltip") : depth0), depth0))
    + "\"";
},"10":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " class=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"variant") : depth0), depth0))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"icon-container left\"> "
    + alias2(lookupProperty(helpers,"getIcon").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"icon") : stack1),{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":34},"end":{"line":1,"column":56}}}))
    + " </div> <div class=\"background\"></div> <div class=\"label-container\"> <div class=\"label\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"accessibilityId") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":143},"end":{"line":1,"column":219}}})) != null ? stack1 : "")
    + "></div> </div> <div class=\"prefix\"></div> <input type=\""
    + alias2(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"inputType") : stack1), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"accessibilityId") : stack1),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":294},"end":{"line":1,"column":383}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"autocomplete") : stack1),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":383},"end":{"line":1,"column":548}}})) != null ? stack1 : "")
    + "/> <div class=\"suffix\"></div> <div class=\"icon-container right\"> "
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"actions") : stack1),{"name":"each","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":613},"end":{"line":1,"column":796}}})) != null ? stack1 : "")
    + " </div> <div class=\"border-container\"></div>";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Textbox.js.map